import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Reveal from '../components/Reveal';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { useSEO } from '../seo';

/**
 * Home is intentionally English-only. The marketing copy here is the
 * primary brand surface and we want a single, opinionated voice — i18n
 * for translated long-form content (terms, privacy, FAQ) still works
 * as before, but the landing page itself does not switch.
 */
export default function Home() {
  const seo = useSEO({
    title: 'Doodi · Couple balance game',
    description:
      "Once a day, a moment to know each other better. Doodi sends a fresh question every day. Answer it, guess your partner's pick, and watch your sync rate grow.",
    path: '/',
  });

  return (
    <>
      {seo}
      <Hero />
      <HowItWorks />
      <Features />
      <Values />
      <BottomCta />
    </>
  );
}

function Hero() {
  const reduce = usePrefersReducedMotion();
  const blob1 = useRef<HTMLSpanElement>(null);
  const blob2 = useRef<HTMLSpanElement>(null);
  const blob3 = useRef<HTMLSpanElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  // Parallax: as the page scrolls, the coloured blobs drift at
  // different rates against the foreground content. Skip under
  // reduced-motion. Hand-rolled with a rAF gate so we don't queue a
  // React re-render on every scroll tick — direct style writes only.
  useEffect(() => {
    if (reduce) return undefined;
    let ticking = false;
    const apply = () => {
      const y = window.scrollY;
      if (blob1.current) {
        blob1.current.style.transform = `translate3d(0, ${y * 0.28}px, 0)`;
      }
      if (blob2.current) {
        blob2.current.style.transform = `translate3d(0, ${y * -0.18}px, 0)`;
      }
      if (blob3.current) {
        blob3.current.style.transform = `translate3d(0, ${y * 0.12}px, 0)`;
      }
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
    return () => window.removeEventListener('scroll', onScroll);
  }, [reduce]);

  // Phone mouse-tilt: tracks cursor across the whole hero section
  // (not just the phone itself) so the effect picks up early as a
  // visitor moves their pointer over the headline.
  useEffect(() => {
    if (reduce) return undefined;
    const hero = heroRef.current;
    const phone = phoneRef.current;
    if (!hero || !phone) return undefined;
    // Skip on coarse pointers (touch devices) — tilt without a
    // hovered cursor just looks broken.
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;

    let raf = 0;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = hero.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (e.clientX - cx) / rect.width;
        const dy = (e.clientY - cy) / rect.height;
        const max = 5; // degrees
        phone.style.transform = `perspective(1100px) rotateY(${dx * max}deg) rotateX(${-dy * max}deg)`;
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      phone.style.transform = '';
    };
    hero.addEventListener('mousemove', onMove);
    hero.addEventListener('mouseleave', onLeave);
    return () => {
      hero.removeEventListener('mousemove', onMove);
      hero.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, [reduce]);

  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden pt-20 pb-16 md:pt-32 md:pb-24"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <span
          ref={blob1}
          className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-doodi-coral opacity-35 blur-3xl will-change-transform"
        />
        <span
          ref={blob2}
          className="absolute -bottom-40 -left-24 h-[320px] w-[320px] rounded-full bg-doodi-mint opacity-25 blur-3xl will-change-transform"
        />
        <span
          ref={blob3}
          className="absolute right-[30%] top-[40%] h-[280px] w-[280px] rounded-full bg-doodi-sun opacity-20 blur-3xl will-change-transform"
        />
      </div>

      <div className="container-page relative z-10 grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:gap-16">
        <Reveal>
          <p className="mb-4 inline-flex items-center gap-2 text-sm uppercase tracking-wider text-ink-3">
            <span className="rounded-full bg-doodi-coral-soft px-2 py-0.5 text-xs font-bold tracking-wide text-doodi-coral-deep">
              Beta
            </span>
            Couple balance game
          </p>
          <h1 className="mb-6 font-serif text-4xl font-semibold leading-[1.1] tracking-[-0.03em] text-ink sm:text-5xl md:text-6xl lg:text-7xl">
            Once a day,
            <br />
            a moment to know each other better.
          </h1>
          <p className="mb-8 max-w-[34rem] text-lg leading-relaxed text-ink-3">
            Doodi sends a fresh question every day. Answer it, guess your
            partner's pick, and watch your sync rate grow.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/download" className={btnPrimary}>
              Download
            </Link>
            <a href="#how" className={btnGhost}>
              How it works
            </a>
          </div>
        </Reveal>

        <Reveal delay={160} className="hidden justify-self-end lg:block">
          <div
            ref={phoneRef}
            className="transition-transform duration-200 ease-[var(--ease-out-soft)] will-change-transform"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <DemoPhone />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps: Array<{ title: string; body: string }> = [
    {
      title: '1. Pair with a code',
      body: "Share your 8-character couple code, or enter your partner's. One code, one partner.",
    },
    {
      title: '2. One question, every day',
      body: 'At the time you pick, both of you answer — and try to guess what the other will choose.',
    },
    {
      title: '3. Build a sync rate together',
      body: 'See your weekly trend, category strengths, streaks, and prediction accuracy at a glance.',
    },
  ];

  return (
    <section id="how" className="border-y border-line bg-paper py-20 md:py-32">
      <div className="container-page">
        <SectionHead
          title="How it works"
          subtitle="No setup overhead. One minute a day."
        />
        <ol className="grid list-none gap-8 p-0 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal
              key={i}
              as="li"
              delay={i * 100}
              className="rounded-3xl border border-line bg-card p-8 shadow-paper-1"
            >
              <span className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-bg-warm font-mono text-xs font-bold tracking-wide text-doodi-coral-deep">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="mb-2 font-serif text-xl font-semibold text-ink">
                {s.title}
              </h3>
              <p className="m-0 text-ink-3">{s.body}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Features() {
  const items: Array<{
    icon: 'f1' | 'f2' | 'f3' | 'f4';
    tone: string;
    title: string;
    body: string;
  }> = [
    {
      icon: 'f1',
      tone: 'bg-doodi-coral-soft text-doodi-coral-deep',
      title: 'Pokes',
      body: 'Up to ten gentle nudges a day when your partner is taking their time.',
    },
    {
      icon: 'f2',
      tone: 'bg-doodi-mint-soft text-doodi-mint-deep',
      title: 'Comments that turn into chats',
      body: 'Drop a line under each result — a quick joke, an honest aside.',
    },
    {
      icon: 'f3',
      tone: 'bg-doodi-sun-soft text-[#C58A1F]',
      title: 'Anniversary calendar',
      body: '100 days, anniversaries, birthdays — auto-marked, plus your own custom dates.',
    },
    {
      icon: 'f4',
      tone: 'bg-doodi-sky-soft text-[#4F77C4]',
      title: 'Stats & trends',
      body: '14-day sync trend, percentile vs other couples, prediction accuracy.',
    },
  ];

  return (
    <section className="py-20 md:py-32">
      <div className="container-page">
        <SectionHead title="Small things that make it click" />
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {items.map((it, i) => (
            <Reveal
              key={i}
              as="article"
              delay={i * 80}
              className="rounded-3xl border border-line bg-card p-8 transition hover:-translate-y-0.5 hover:shadow-paper-2"
            >
              <span
                className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${it.tone}`}
              >
                <FeatureIcon variant={it.icon} />
              </span>
              <h3 className="mb-2 font-serif text-xl font-semibold text-ink">
                {it.title}
              </h3>
              <p className="m-0 text-ink-3">{it.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Values() {
  const items = [
    {
      title: 'A space just for two',
      body: 'No public feed, no friend graph. Your record stays between the two of you.',
    },
    {
      title: 'Honest about your data',
      body: 'Unpair and every answer, comment, and event is permanently deleted.',
    },
    {
      title: 'Light, daily',
      body: "One minute is enough. You don't need to live in the app.",
    },
  ];

  return (
    <section className="border-y border-line bg-paper py-20 md:py-32">
      <div className="container-prose">
        <SectionHead title="What Doodi believes in" center />
        <div className="mt-6 grid gap-8 sm:grid-cols-3">
          {items.map((v, i) => (
            <Reveal key={i} delay={i * 100} className="p-4 text-center">
              <h3 className="mb-2 font-serif text-xl font-semibold text-ink">
                {v.title}
              </h3>
              <p className="m-0 text-ink-3">{v.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BottomCta() {
  return (
    <section
      id="cta"
      className="py-20 text-center md:py-32"
      style={{
        backgroundImage:
          'radial-gradient(ellipse at top, var(--color-doodi-coral-soft) 0%, transparent 60%)',
      }}
    >
      <div className="container-prose">
        <Reveal>
          <h2 className="mb-3 font-serif text-3xl font-semibold tracking-tight md:text-5xl">
            Start today, with Doodi.
          </h2>
          <p className="mb-8 text-lg text-ink-3">
            One question a day. One minute, together.
          </p>
          <Link
            to="/download"
            className={`${btnPrimary} px-8 py-4 text-lg`}
          >
            Download Doodi
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

const btnBase =
  'inline-flex items-center justify-center gap-2 rounded-full border border-transparent px-5 py-3 text-base font-semibold no-underline transition-all duration-150 ease-[var(--ease-out-soft)]';
const btnPrimary = `${btnBase} bg-ink text-white hover:-translate-y-px hover:bg-doodi-coral-deep hover:shadow-paper-2`;
const btnGhost = `${btnBase} border-line-strong bg-transparent text-ink hover:-translate-y-px hover:border-ink`;

function SectionHead({
  title,
  subtitle,
  center,
}: {
  title: string;
  subtitle?: string;
  center?: boolean;
}) {
  return (
    <Reveal
      as="header"
      className={`mb-12 max-w-[40rem] ${center ? 'mx-auto text-center' : ''}`}
    >
      <h2 className="mb-3 font-serif text-3xl font-semibold tracking-tight text-ink md:text-5xl">
        {title}
      </h2>
      {subtitle && <p className="m-0 text-lg text-ink-3">{subtitle}</p>}
    </Reveal>
  );
}

function FeatureIcon({ variant }: { variant: 'f1' | 'f2' | 'f3' | 'f4' }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 22,
    height: 22,
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (variant) {
    case 'f1':
      return (
        <svg {...common}>
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M19 5l-3 3M8 16l-3 3" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'f2':
      return (
        <svg {...common}>
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        </svg>
      );
    case 'f3':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
          <path d="m12 13 1.5 3 3 .4-2.2 2.1.5 3-2.8-1.5-2.8 1.5.5-3L7.5 16.4l3-.4z" />
        </svg>
      );
    case 'f4':
      return (
        <svg {...common}>
          <path d="M3 20h18" />
          <path d="M6 16V9M11 16V5M16 16v-7M21 16v-4" />
        </svg>
      );
  }
}

function DemoPhone(): ReactNode {
  // Static, stylised question card — communicates "balance game" at a glance.
  return (
    <div
      className="relative flex h-[600px] w-[320px] flex-col gap-6 rounded-[44px] border border-line-strong bg-card p-6"
      style={{
        boxShadow:
          '0 30px 80px rgba(43,43,58,0.12), 0 8px 16px rgba(43,43,58,0.06), inset 0 0 0 8px var(--color-bg)',
      }}
    >
      <div
        className="absolute left-1/2 top-3.5 h-6 w-24 -translate-x-1/2 rounded-full bg-ink"
        aria-hidden
      />
      <div className="mt-6 flex h-full flex-col gap-5">
        <div className="flex items-center justify-between text-xs">
          <span className="rounded-full bg-bg-warm px-2.5 py-1 font-bold tracking-wide text-ink-3">
            Q · 042
          </span>
          <span className="text-ink-muted">9:00</span>
        </div>
        <p className="m-0 mt-1 font-serif text-xl font-bold leading-snug tracking-tight text-ink">
          Travel, or staying in?
          <br />
          Pick one.
        </p>
        <div className="mt-auto flex flex-col gap-3">
          <div className="rounded-full border-[1.5px] border-doodi-coral bg-doodi-coral-soft px-4 py-4 text-center text-base font-semibold text-doodi-coral-deep">
            Travel
          </div>
          <div className="rounded-full border-[1.5px] border-transparent bg-bg-warm px-4 py-4 text-center text-base font-semibold text-ink-2">
            Staying in
          </div>
        </div>
        <div className="flex items-center gap-3 border-t border-line pt-4">
          <span className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-bg-warm">
            <span
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                width: '82%',
                background:
                  'linear-gradient(90deg, var(--color-doodi-coral), var(--color-doodi-coral-deep))',
              }}
            />
          </span>
          <span className="text-sm font-bold text-doodi-coral-deep">82%</span>
        </div>
      </div>
    </div>
  );
}
